import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormsValidators, yesNoOptions } from '@msslib/components';
import { FormFieldType, WrapperType } from '@msslib/components/formly/formly.config';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IPropertyAndLoan,
  errorMessages,
  initialProductLengthTooltip,
} from 'apps/clubhub/src/app/ignite/models/affordability';
import { IApplicants, IMortgageRequirements } from 'apps/clubhub/src/app/ignite/models/products';
import {
  ApplicantCountry,
  BtlType,
  HolidayLetFrequency,
  MortgageLenders,
  MortgageType,
  ProductLengthExtended,
  PropertyType,
  RemortgageType,
  RepaymentMethod,
  TaxStatusBtl,
  UkLocation,
} from 'apps/shared/src/models';
import { ProductsSearchService } from '../products-search.service';
import { IgniteHelperService, LtvService } from '@msslib/services';
import { LenderService } from 'apps/shared/src/services/lender.service';
import { map } from 'rxjs';
import { productFiltersNames } from 'apps/clubhub/src/app/constants';

const secondChargeLoanAmountLabel = 'Second Charge Loan Amount';
const loanAmountLabel = 'Loan Amount';
const defaultNumberOfApplicants = 1;

@Injectable({
  providedIn: 'root',
})
export class ProductsFormFieldsBtlService {
  public constructor(
    private productsSearchService: ProductsSearchService,
    private helperService: IgniteHelperService,
    private lenderService: LenderService,
    private ltvService: LtvService,
  ) { }

  public resetFieldIfEmpty(field: FormlyFieldConfig) {
    if (!field.model) {
      field.formControl?.reset();
    }
  }

  public keyboardEventMarkTouched(field: FormlyFieldConfig, event: KeyboardEvent) {
    const key = event.key;
    if (!['Tab', 'Shift'].includes(key)) {
      field.formControl?.markAsTouched();
    }
  }

  public fields: FormlyFieldConfig[] = [
    {
      key: 'mortgageRequirements',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Mortgage Details',
      },
      validators: {
        max500PercentLoanValue: {
          expression: FormsValidators.expression(FormsValidators.ltv(500)),
          message: 'Loan amount should not exceed 500% of property value',
          errorPath: 'loanAmount',
        },
        lessThanDeposit: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, propertyValue }
              = field.form?.getRawValue().mortgageRequirements as IMortgageRequirements;
            const equityLoanValue = control.value.equityLoanValue;
            if (!equityLoanValue || !propertyValue || !loanAmount) {
              return true;
            }

            return equityLoanValue < propertyValue - loanAmount;
          },
          message: 'Equity loan value must be less than deposit',
          errorPath: 'equityLoanValue',
        },
        interestOnlyAmountValidator: {
          expression: (control: AbstractControl) => {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const repaymentMethodValue = control['controls']?.repaymentMethod?.value?.value;
            if (repaymentMethodValue === RepaymentMethod.InterestOnlyPartAndPart) {
              const validator = FormsValidators.expression(FormsValidators
                .greaterThanField<IMortgageRequirements>('interestOnlyAmount', 'loanAmount', true));
              return validator(control);
            }

            return true;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          key: 'limitedCompanyPurchase',
          type: 'radio-button',
          props: {
            label: 'Limited company purchase',
            options: yesNoOptions,
          },
        },
        {
          key: 'limitedCompanySpecialPurposeVehicle',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Is the Limited company in a Special Purpose Vehicle?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.limitedCompanyPurchase !== true,
          },
        },
        {
          key: 'portfolioLandlord',
          type: 'radio-button',
          props: {
            tooltip: '4 or more Buy to Let or Consent to Let mortgaged properties (including this application)',
            label: 'Portfolio landlord',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.limitedCompanyPurchase !== false,
          },
        },
        {
          key: 'numberOfProperties',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'How many properties in the portfolio?',
            min: 4,
          },
          expressions: {
            hide: field => !field.model?.portfolioLandlord,
          },
        },
        {
          key: 'location',
          type: FormFieldType.Select,
          defaultValue: null,
          props: {
            label: 'Property location',
            options: [
              this.helperService.defaultSelectOption,
              { value: UkLocation.EastMidlands, label: 'East Midlands' },
              { value: UkLocation.EastOfEngland, label: 'East of England' },
              { value: UkLocation.GreaterLondon, label: 'Greater London' },
              { value: UkLocation.NorthEast, label: 'North East' },
              { value: UkLocation.NorthWest, label: 'North West' },
              { value: UkLocation.SouthEast, label: 'South East' },
              { value: UkLocation.SouthWest, label: 'South West' },
              { value: UkLocation.WestMidlands, label: 'West Midlands' },
              { value: UkLocation.YorkshireAndHumberside, label: 'Yorkshire & Humberside' },
              { value: UkLocation.NorthernIreland, label: 'Northern Ireland' },
              { value: UkLocation.Scotland, label: 'Scotland' },
              { value: UkLocation.Wales, label: 'Wales' },
            ],
          },
        },
        {
          key: 'btlType',
          type: 'select',
          props: {
            label: 'BTL type',
            options: [
              { label: 'Standard', value: BtlType.Standard },
              { label: 'HMO', value: BtlType.HMO },
              { label: 'Holiday let', value: BtlType.HolidayLet },
              { label: 'MUFB', value: BtlType.MUFB },
            ],
            required: true,
          },
        },
        {
          key: 'numberOfBedrooms',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'Number of bedrooms',
            min: 2,
            max: 20,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.HMO,
          },
        },
        {
          key: 'numberOfBlocks',
          type: FormFieldType.Number,
          defaultValue: 0,
          props: {
            label: 'Number of units',
            min: 2,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'propertySelfContainedFlats',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Does the property have self contained flats?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'New build',
            options: yesNoOptions,
          },
        },
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
          expressions: {
            hide: field => !field.model?.newBuild,
          },
        },
        {
          key: 'mortgageType',
          type: 'radio-button',
          props: {
            label: 'Mortgage type',
            options: this.getMortgageTypeOptions(),
            required: true,
          },
        },
        {
          key: 'remortgageType',
          type: 'select',
          props: {
            label: 'Remortgage type',
            allowNull: true,
            options: [
              { label: 'Let to buy', value: RemortgageType.LetToBuy },
              { label: 'Like for like', value: RemortgageType.LikeForLike },
              { label: 'Capital raising', value: RemortgageType.CapitalRaising },
            ],
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Remortgage,
          },
        },
        {
          key: 'firstTimeBuyer',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time buyer',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'firstTimeLandlord',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time landlord',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'ownerOccupier',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Owner occupier',
            options: yesNoOptions,
          },
          expressions: {
            hide: ({ model }: { model: IPropertyAndLoan }) =>
              model?.limitedCompanyPurchase !== false
              && model?.firstTimeBuyer !== false,
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'propertyValue',
          type: 'currency',
          props: {
            label: 'Property value',
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.propertyValueDisabled,
          },
        },
        {
          key: 'loanAmount',
          type: 'currency',
          props: {
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
            'props.label': field => {
              return (field.model as IPropertyAndLoan)?.mortgageType === MortgageType.SecondCharge
                ? secondChargeLoanAmountLabel
                : loanAmountLabel;
            },
          },
        },
        {
          key: 'outstandingBalanceCurrentMortgage',
          type: 'currency',
          props: {
            label: 'Outstanding Balance on the Current Mortgage',
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.SecondCharge,
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
          },
        },
        {
          key: 'ltv',
          type: 'disabled',
          props: {
            label: 'LTV',
            addonEnd: '%',
          },
          expressions: {
            'props.value': field =>
              this.ltvService.calculateLtv((field.parent?.model ?? {}) as IMortgageRequirements),
          },
        },
        {
          key: 'repaymentMethod',
          props: {
            label: 'Method of repayment',
          },
          fieldGroup: [
            {
              key: 'value',
              type: 'select',
              props: {
                allowNull: true,
                options: [
                  { label: 'Capital and interest', value: RepaymentMethod.CapitalAndInterest },
                  { label: 'Interest only', value: RepaymentMethod.InterestOnly },
                  { label: 'Interest only part and part', value: RepaymentMethod.InterestOnlyPartAndPart },
                ],
                collapseMargin: true,
              },
            },
          ],
        },
        {
          key: 'interestOnlyAmount',
          type: 'currency',
          props: {
            label: 'Amount on interest only',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          expressions: {
            hide: field => (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value
              !== RepaymentMethod.InterestOnlyPartAndPart,
          },
        },
        {
          key: 'mortgageTerm',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            label: 'Mortgage term',
            suppressErrors: true,
          },
          fieldGroup: [
            {
              key: 'years',
              type: 'number',
              className: 'col',
              wrappers: [WrapperType.NoWrappers],
              props: {
                min: 0,
                max: 40,
                addonEnd: 'years',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'months',
              type: 'number',
              className: 'col',
              wrappers: [WrapperType.NoWrappers],
              props: {
                min: 0,
                max: 11,
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
          validators: {
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value ?? {};
                if (!years) {
                  return true;
                }
                return 12 * years + (months ?? 0) <= 40 * 12;
              },
              message: 'Mortgage term has a maximum of 40 years',
            },
          },
        },
        {
          key: 'holidayLetIncome',
          expressions: {
            hide: field => (field.parent?.model as IPropertyAndLoan)?.btlType !== BtlType.HolidayLet,
          },
          props: {
            suppressErrors: true,
          },
          fieldGroup: [
            {
              type: FormFieldType.Separator,
              props: {
                header: 'Holiday Let Income',
                headerTooltip: 'Enter expected Low, Mid, and High seasonal rentals',
              },
            },
            {
              key: 'holidayLetIncomeLow',
              type: FormFieldType.Currency,
              props: {
                label: 'Low season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeMedium',
              type: FormFieldType.Currency,
              props: {
                label: 'Mid season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeHigh',
              type: FormFieldType.Currency,
              props: {
                label: 'High season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeFrequency',
              type: FormFieldType.Select,
              defaultValue: null,
              props: {
                options: this.helperService.buildSelectOptionsFromEnum(HolidayLetFrequency),
                label: 'Payment frequency',
              },
            },
            {
              template: `
                <hr class="mt-4" />
              `,
            },
          ],
        },
        {
          key: 'rentalAmountReceivedExpected',
          type: FormFieldType.Currency,
          props: {
            label: 'Monthly rental amount received / expected',
            tooltip: 'What is the monthly rental amount received / expected if let on Assured Shorthold Tenancy basis',
          },
        },
        {
          key: 'productLengths',
          type: FormFieldType.MultiSelect,
          defaultValue: [],
          props: {
            label: productFiltersNames.initialProductPeriod,
            tooltip: initialProductLengthTooltip,
            options: this.helperService.productLengthExtendedOptions(),
            required: false,
          },
        },
        {
          key: 'customProductLength',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            suppressErrors: true,

          },
          expressions: {
            hide: field => !(field.parent?.model as IMortgageRequirements)?.productLengths?.includes(
              ProductLengthExtended.Custom),
          },
          fieldGroup: [
            {
              key: 'from',
              type: 'number',
              className: 'col',
              name:'From',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'From',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'to',
              type: 'number',
              className: 'col',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'To',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
        },
        {
          key: 'rateTypes',
          type: FormFieldType.MultiSelect,
          defaultValue: [],
          props: {
            label: 'Rate Type',
            options: this.helperService.rateTypesOptions,
            required: false,
          },
        },
        {
          type: FormFieldType.Separator,
          props: {
            header: 'Applicants',
          },
        },
        {
          key: 'applicants',
          wrappers: [WrapperType.EmptyWrapper],
          fieldGroup: [
            {
              key: 'numberOfApplicants',
              type: FormFieldType.RadioButtons,
              defaultValue: defaultNumberOfApplicants,
              props: {
                label: 'Number of applicants',
                options: [
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                ],
                required: false,
                change: (field: FormlyFieldConfig) => {
                  const numberOfApplicants = +field.formControl?.value;
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
              hooks: {
                onInit: (field) => {
                  const numberOfApplicants = field.formControl?.value ?? defaultNumberOfApplicants;
                  field.formControl?.setValue(numberOfApplicants);
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
            },
            {
              key: 'applicantAges',
              type: FormFieldType.Repeating,
              props: {
                suppressErrors: true,
                id: 'applicantAges',
              },
              fieldArray: {
                wrappers: [WrapperType.NoWrappers],
                props: {
                  suppressErrors: true,
                },
                fieldGroup: [
                  {
                    expressions: {
                      template: field => {
                        return `
                          <hr class="my-0" />
                          <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                        `;
                      },
                    },
                  },
                  {
                    key: 'dateOfBirth',
                    type: FormFieldType.Date,
                    props: {
                      label: 'Date of birth',
                    },
                    validators: {
                      validation: [FormsValidators.dateOrEmpty, FormsValidators.atLeast18YearsOrEmpty],
                    },
                  },
                  {
                    key: 'countryResident',
                    type: FormFieldType.Select,
                    defaultValue: null,
                    props: {
                      label: 'What country is the applicant currently resident in',
                      options: this.helperService.countryResidentTypeOptions,
                    },
                  },
                  {
                    key: 'grossAnnualIncome',
                    type: FormFieldType.Currency,
                    props: {
                      label: 'Gross Annual Income',
                      tooltip: 'Please include total of all income before tax.',
                    },
                  },
                  {
                    key: 'taxStatusBtl',
                    type: FormFieldType.Select,
                    defaultValue: null,
                    props: {
                      label: 'Tax status',
                    },
                    validators: {
                      validation: [FormsValidators.isNullOrEnumMember(TaxStatusBtl)],
                    },
                    expressions: {
                      'props.options': (field) => {
                        const countryResident  = field.model?.countryResident;
                        if (countryResident === ApplicantCountry.Scotland) {
                          return this.helperService.buildSelectOptionsFromEnum(TaxStatusBtl)
                            .filter((option) => option.value !== TaxStatusBtl.Additional);
                        }
                        return this.helperService
                          .buildSelectOptionsFromEnum(TaxStatusBtl)
                          .filter(
                            (option) =>
                              option.value !== TaxStatusBtl.Top &&
                              option.value !== TaxStatusBtl.Starter &&
                              option.value !== TaxStatusBtl.Intermediate,
                          );
                      },
                      hide: field => field.model?.countryResident === null,
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'getResultsBtn',
          type: 'buttons',
          props: {
            buttons: [
              { label: 'Get results', type: 'submit', className: 'btn-2022--secondary' },
            ],
          },
        },
      ],
    },
  ];

  private onNumberOfApplicantsChange(numberOfApplicants: number) {
    if (!this.productsSearchService.model) {
      this.productsSearchService.updateModel();
    }

    const model = this.productsSearchService.model;

    if (!model.mortgageRequirements?.applicants) {
      model.mortgageRequirements.applicants = {} as IApplicants;
    }

    if (!model.mortgageRequirements.applicants.applicantAges) {
      model.mortgageRequirements.applicants.applicantAges
        = this.helperService.generateEmptyApplicantAges(numberOfApplicants);
      this.productsSearchService.updateModel(this.productsSearchService.model);
      return;
    }

    const applicantAges = model.mortgageRequirements.applicants.applicantAges;

    if (applicantAges.length > numberOfApplicants) {
      model.mortgageRequirements.applicants.applicantAges = applicantAges?.slice(0, numberOfApplicants);
    } else if (applicantAges.length < numberOfApplicants) {
      const emptyApplicantAges = this.helperService.generateEmptyApplicantAges(
        numberOfApplicants - applicantAges.length,
        applicantAges.length,
      );
      model.mortgageRequirements.applicants.applicantAges.push(...emptyApplicantAges);
    }

    this.productsSearchService.updateModel(this.productsSearchService.model);
  }

  private getMortgageTypeOptions() {
    return [
      { label: 'Purchase', value: MortgageType.Purchase },
      { label: 'Remortgage', value: MortgageType.Remortgage },
      { label: 'Second Charge', value: MortgageType.SecondCharge },
    ];
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field) => {
      return field?.model?.mortgageType !== MortgageType.Remortgage
        && (field?.model?.mortgageType !== MortgageType.Purchase || field?.model?.firstTimeBuyer !== false);
    };

    const initialPeriodHidden = (field) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.NoWrappers],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage Lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService.getUnarchivedLendersNames(this.helperService.getBtlLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the Initial period?',
        },
        validators: {
          validation: [FormsValidators.dateOrEmpty],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }
}
